import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { ExportMenu } from "@/components/ExportMenu";
import { PreviewButton } from "@/components/PreviewButton";
import { ToolBar } from "@/components/ToolBar";
import { LineItemsEditor, computeLineTotal } from "@/components/LineItemsEditor";
import { OperationalCategoryEditor } from "@/components/OperationalCategoryEditor";
import { itemsToCategoryMap, categoryMapToItems, OPERATIONAL_CATEGORY_ROWS, CASH_VOUCHER_CATEGORIES } from "@/lib/budgetCategories";
import { Plus, CheckCircle, XCircle, PencilSimple, Upload, Paperclip, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";
import { DiscussButton } from "@/components/DiscussButton";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "rejected", label: "Rejected" },
];

const EMPTY_OP_MAP = () => Object.fromEntries(
  OPERATIONAL_CATEGORY_ROWS.map((r) => [r.key, { amount: 0, description: "" }])
);

const EMPTY = { title: "", department: "", purpose: "", items: [], request_date: "", request_type: "operational", personnel: [], notes: "", bank_name: "", payee: "", check_number: "", cash_voucher_category: "", attachments: [] };

function authedHref(url) {
  if (!url) return "#";
  if (url.startsWith("/api/files/")) {
    const t = localStorage.getItem("access_token") || "";
    return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${encodeURIComponent(t)}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

const TYPE_OPTIONS = [
  { value: "operational", label: "Operational Request", prefix: "OR" },
  { value: "cash_voucher", label: "Cash Voucher", prefix: "CV" },
  { value: "check_voucher", label: "Check Voucher", prefix: "CHV" },
];
// Legacy label kept so existing BR-xxxx rows still render their type badge correctly.
const TYPE_LABEL = { budget: "Budget Request", ...Object.fromEntries(TYPE_OPTIONS.map((t) => [t.value, t.label])) };

export default function BudgetRequestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [personnelText, setPersonnelText] = useState("");
  const [form, setForm] = useState(EMPTY);
  // Operational Request only: amount-per-category map. Kept as a separate
  // state so switching request types doesn't clobber the line-items list.
  const [opMap, setOpMap] = useState(EMPTY_OP_MAP);
  const [uploading, setUploading] = useState(false);

  async function uploadFiles(files) {
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(`${API_BASE}/uploads/billing`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        });
        if (!res.ok) throw new Error("upload failed");
        uploaded.push(await res.json());
      }
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] }));
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  }

  // review dialog
  const [reviewing, setReviewing] = useState(null);
  const [reviewStatus, setReviewStatus] = useState("in_progress");
  const [reviewNote, setReviewNote] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (showArchived) params.include_archived = true;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/budget-requests", { params });
      setItems(data);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, showArchived]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const filtered = items.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.title?.toLowerCase().includes(q) ||
      b.department?.toLowerCase().includes(q) ||
      b.request_no?.toLowerCase().includes(q) ||
      b.purpose?.toLowerCase().includes(q) ||
      b.payee?.toLowerCase().includes(q) ||
      b.notes?.toLowerCase().includes(q) ||
      b.submitted_by_name?.toLowerCase().includes(q) ||
      (b.items || []).some((it) => it.description?.toLowerCase().includes(q)) ||
      (b.personnel || []).some((p) => String(p).toLowerCase().includes(q)) ||
      String(b.total_amount ?? b.amount ?? "").includes(q)
    );
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setOpMap(EMPTY_OP_MAP()); setPersonnelText(""); setOpen(true); }
  function openEdit(b) {
    setEditing(b);
    const personnel = b.personnel || b.technicians || [];
    setForm({
      title: b.title, department: b.department, purpose: b.purpose,
      items: b.items || [],
      request_date: b.request_date || "",
      request_type: b.request_type || "budget",
      personnel,
      notes: b.notes || "",
      bank_name: b.bank_name || "",
      payee: b.payee || "",
      check_number: b.check_number || "",
      cash_voucher_category: b.cash_voucher_category || "",
      attachments: b.attachments || [],
    });
    setOpMap(
      (b.request_type || "budget") === "operational"
        ? itemsToCategoryMap(b.items || [])
        : EMPTY_OP_MAP()
    );
    setPersonnelText(personnel.join(", "));
    setOpen(true);
  }

  async function submit(e) {
    e.preventDefault();
    const personnel = personnelText.split(",").map((s) => s.trim()).filter(Boolean);
    // Operational Requests derive their items from the category-amount map.
    const items = form.request_type === "operational"
      ? categoryMapToItems(opMap)
      : form.items;
    const payload = { ...form, items, personnel, total_amount: computeLineTotal(items) };
    // Only Cash Vouchers carry an accounting category — strip elsewhere so the
    // field stays scoped and doesn't pollute non-CV records.
    if (form.request_type !== "cash_voucher") payload.cash_voucher_category = null;
    else if (!payload.cash_voucher_category) payload.cash_voucher_category = null;
    try {
      if (editing) {
        await api.put(`/budget-requests/${editing.id}`, payload);
        toast.success("Request updated");
      } else {
        await api.post("/budget-requests", payload);
        toast.success("Request submitted");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  }

  async function submitReview() {
    try {
      await api.post(`/budget-requests/${reviewing.id}/review`, { status: reviewStatus, note: reviewNote });
      toast.success(`Marked ${reviewStatus.replace("_", " ")}`);
      setReviewing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to review");
    }
  }

  const canReview = can(user, ["finance", "admin"]);

  return (
    <div className="space-y-6" data-testid="budget-requests-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Module 01</div>
          <h1 className="section-title text-3xl sm:text-4xl">Requests &amp; Vouchers</h1>
          <p className="text-sm text-muted-foreground mt-1">File operational requests, cash vouchers, and check vouchers — track their approvals across departments.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-budget-btn">
          <Plus size={16} className="mr-2" /> New Request
        </Button>
      </div>

      <ToolBar
        search={search} onSearch={setSearch}
        status={status} onStatus={setStatus} statusOptions={STATUS_OPTIONS}
        placeholder="Search title, dept, request no, payee, purpose, items… (all statuses)"
        showArchived={showArchived} onToggleArchived={setShowArchived}
      />

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[1200px]">
          <TableHeader>
            <TableRow>
              <TableHead>Request No.</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Personnel</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Submitted By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-8">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-8">No requests or vouchers found.</TableCell></TableRow>
            ) : filtered.map((b) => (
              <TableRow key={b.id} data-testid={`budget-row-${b.id}`}>
                <TableCell className="font-mono text-xs">
                  <span className="inline-flex items-center gap-1.5">{b.request_no}
                    {(b.attachments || []).length > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground" title={`${b.attachments.length} attachment(s)`} data-testid={`budget-attach-count-${b.id}`}>
                        <Paperclip size={11} />{b.attachments.length}
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-xs">
                  <span className="inline-block px-1.5 py-0.5 border border-border rounded font-medium">{TYPE_LABEL[b.request_type || "budget"] || "Budget Request"}</span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground tabular-nums">{b.request_date || "—"}</TableCell>
                <TableCell className="font-medium">{b.title}</TableCell>
                <TableCell>{b.department}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" title={(b.personnel || b.technicians || []).join(", ")}>
                  {((b.personnel || b.technicians || []).length) ? (b.personnel || b.technicians).join(", ") : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">₱{(b.total_amount || 0).toLocaleString()}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{b.submitted_by_name}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <div className="flex justify-end gap-2">
                    {canReview && ["open", "in_progress"].includes(b.status) && (
                      <Button size="sm" variant="outline" onClick={() => { setReviewing(b); setReviewStatus(b.status === "in_progress" ? "completed" : "in_progress"); setReviewNote(""); }} data-testid={`review-budget-${b.id}`} className="border-border">
                        <CheckCircle size={14} className="mr-1" /> {b.status === "in_progress" ? "Update" : "Review"}
                      </Button>
                    )}
                    {(b.submitted_by === user?.id || user?.role === "admin") && ["open", "in_progress"].includes(b.status) && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`edit-budget-${b.id}`} className="border-border">
                        <PencilSimple size={14} />
                      </Button>
                    )}
                    <PreviewButton endpointPath={`/exports/budget-request/${b.id}`} baseName={`budget_${b.request_no}`} title={`${b.request_type === "cash_voucher" ? "Cash Voucher" : b.request_type === "check_voucher" ? "Check Voucher" : b.request_type === "operational" ? "Operational Request" : "Budget Request"} — ${b.request_no}`} testId={`preview-budget-${b.id}`} />
                    <ExportMenu endpointPath={`/exports/budget-request/${b.id}`} baseName={`budget_${b.request_no}`} testId={`export-budget-${b.id}`} />
                    <DiscussButton recordType="budget_request" recordId={b.id} label={b.request_no} testId={`discuss-budget-${b.id}`} />
                    {(user?.role === "admin" || b.submitted_by === user?.id) && !b.deleted_at && (
                      <ArchiveButton endpoint={`/budget-requests/${b.id}`} label={b.request_no} onArchived={load} testId={`archive-budget-${b.id}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              {editing
                ? `Edit ${editing.request_no}`
                : `New ${(TYPE_OPTIONS.find((t) => t.value === form.request_type) || {}).label || "Request"}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border-border" data-testid="budget-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Department</Label>
                <Input required value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="border-border" data-testid="budget-dept-input" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Request Type</Label>
                <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                  <SelectTrigger data-testid="budget-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label} ({t.prefix}-)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Request Date</Label>
                <Input
                  type="date"
                  value={form.request_date || ""}
                  onChange={(e) => setForm({ ...form, request_date: e.target.value })}
                  className="border-border"
                  data-testid="budget-request-date-input"
                />
                <p className="text-[10px] text-muted-foreground">Leave blank for today. Pick a future date to pre-file an upcoming request.</p>
              </div>
              {(form.request_type === "cash_voucher" || form.request_type === "check_voucher") && (
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bank Account</Label>
                  <Input
                    value={form.bank_name || ""}
                    onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                    placeholder="e.g. BDO Unibank, BPI, Metrobank"
                    className="border-border"
                    data-testid="budget-bank-input"
                  />
                  <p className="text-[10px] text-muted-foreground">Vouchers drawn from each bank get their own reference series (e.g. <span className="font-mono">CV-08-0001</span> per bank). Leave blank to use the shared series.</p>
                </div>
              )}
              {form.request_type === "check_voucher" && (
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Check Number</Label>
                  <Input
                    value={form.check_number || ""}
                    onChange={(e) => setForm({ ...form, check_number: e.target.value })}
                    placeholder="e.g. 0012345"
                    className="border-border font-mono"
                    data-testid="budget-check-number-input"
                  />
                  <p className="text-[10px] text-muted-foreground">Physical check serial number. Prints as <b>CHECK NO.</b> on the voucher and appears in the Check Vouchers report for reconciliation with the bank statement.</p>
                </div>
              )}
              {form.request_type === "cash_voucher" && (
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category (optional)</Label>
                  <Select
                    value={form.cash_voucher_category || "__none__"}
                    onValueChange={(v) => setForm({ ...form, cash_voucher_category: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="budget-cv-category-select"><SelectValue placeholder="— none —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— none —</SelectItem>
                      {CASH_VOUCHER_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Accounting bucket printed on the voucher and rolled up in the Cash Voucher — Category Breakdown of period reports.</p>
                </div>
              )}
              {(form.request_type === "cash_voucher" || form.request_type === "check_voucher") && (
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Payee</Label>
                  <Input
                    value={form.payee || ""}
                    onChange={(e) => setForm({ ...form, payee: e.target.value })}
                    placeholder="e.g. Juan Dela Cruz"
                    className="border-border"
                    data-testid="budget-payee-input"
                  />
                  <p className="text-[10px] text-muted-foreground">The assigned personnel who will receive the payment. Prints as <b>PAYEE</b> on the voucher (replaces "Requested By").</p>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Purpose</Label>
              <Textarea required rows={3} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="border-border" data-testid="budget-purpose-input" />
            </div>
            {form.request_type === "operational" ? (
              <OperationalCategoryEditor value={opMap} onChange={setOpMap} />
            ) : (
              <LineItemsEditor items={form.items} onChange={(it) => setForm({ ...form, items: it })} />
            )}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attachments</Label>
              <label className="inline-flex items-center gap-2 text-xs border border-dashed border-border rounded px-3 py-2 hover:bg-muted cursor-pointer" data-testid="budget-upload-label">
                <Upload size={14} />
                {uploading ? "Uploading…" : form.request_type === "cash_voucher" ? "Attach receipts, OR/SI, approvals" : "Attach supporting documents"}
                <input type="file" multiple hidden onChange={(e) => e.target.files && uploadFiles(Array.from(e.target.files))} data-testid="budget-upload-input" />
              </label>
              {(form.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.attachments.map((a, i) => (
                    <div key={i} className="inline-flex items-center gap-1.5 text-xs border border-border rounded px-2 py-1 bg-muted/40" data-testid={`budget-attach-${i}`}>
                      <Paperclip size={12} />
                      <a href={authedHref(a.file_url)} target="_blank" rel="noreferrer" className="hover:underline">{a.file_name}</a>
                      <button type="button" onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, j) => j !== i) }))} className="text-muted-foreground hover:text-foreground">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Personnel Assigned</Label>
              <Input
                value={personnelText}
                onChange={(e) => setPersonnelText(e.target.value)}
                placeholder="e.g. Juan Dela Cruz, Maria Santos, Pedro Reyes"
                className="border-border"
                data-testid="budget-personnel-input"
              />
              <p className="text-[10px] text-muted-foreground">Names of anyone assigned to this request. Separate with commas — spaces inside a name are preserved.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-border" data-testid="budget-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="budget-submit-btn">{editing ? "Save changes" : "Submit request"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewing} onOpenChange={(v) => !v && setReviewing(null)}>
        <DialogContent data-testid="budget-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Review — {reviewing?.request_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 border border-border rounded-md bg-muted/30">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Request</div>
              <div className="font-medium">{reviewing?.title}</div>
              <div className="text-sm text-muted-foreground">{reviewing?.department} · <span className="tabular-nums">₱{(reviewing?.total_amount || 0).toLocaleString()}</span></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Decision</Label>
              <Select value={reviewStatus} onValueChange={setReviewStatus}>
                <SelectTrigger data-testid="review-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_progress">Approve — In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Reject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Note</Label>
              <Textarea rows={3} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} data-testid="review-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button onClick={submitReview} data-testid="review-submit-btn">
              {reviewStatus === "rejected" ? <XCircle size={16} className="mr-2" /> : <CheckCircle size={16} className="mr-2" />}
              Submit review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
